import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';

import type { EstatesOptions } from '@/api/estate-options';
import { Card } from '@/components/ui/card';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import { projectAtom } from '@/features/navigation/atoms/projectAtom';
import { getDate } from '@/utils/getDate';
import { getRenderValue } from '@/utils/getEmptyVisualValue';

import { StatusType } from '../constants/status-type';
import type { EstateCardProps } from './estate-grid-card';

type EstateAdditionalExtendedInfoProps = {
  creator: string;
  createdAt: string;
};

export type EstateLongCardProps = EstateCardProps &
  EstateAdditionalExtendedInfoProps & {
    thumbnailId: string;
    externalId?: string | null;
    marketType?: string;
    propertyType?: string;
    usageType?: string;
    building?: string | null;
    estateOptions?: EstatesOptions;
    showBuilding?: boolean;
    projects?: {
      id: string;
      name: string;
    } | null;
  };

export const EstatesListCard = ({
  estateOptions,
  showBuilding = false,
  ...props
}: EstateLongCardProps) => {
  const { t } = useTranslation();
  const setProject = useSetAtom(projectAtom);

  const params =
    props.propertyLevel === PROPERTY_LEVELS.PROJECT
      ? { projectId: props.id, estateId: '' }
      : { projectId: '', estateId: props.id };
  const linkTo =
    props.propertyLevel === PROPERTY_LEVELS.PROJECT ? '/projects/$projectId' : '/estates/$estateId';

  const { propertyType, marketType } = useMemo(() => {
    let propertyType = props.propertyType;
    let marketType = props.marketType;
    if (propertyType && estateOptions?.propertyType) {
      propertyType =
        estateOptions?.propertyType.find(({ id }) => id === propertyType)?.title || propertyType;
    }
    if (marketType && estateOptions?.marketingType) {
      marketType =
        estateOptions?.marketingType.find(({ id }) => id === marketType)?.title || marketType;
    }
    return { propertyType, marketType };
    // biome-ignore lint/correctness/useExhaustiveDependencies: ignore
  }, [props, estateOptions]);

  return (
    <Link to={linkTo} params={params} onClick={() => setProject(props.projects ?? null)}>
      <Card
        className="flex flex-col items-start gap-4 p-3 transition-all duration-200 hover:shadow-lg sm:flex-row sm:items-center sm:p-4"
        style={{
          boxShadow: '0px 4px 15px 0px #00000040',
        }}
      >
        {/* Thumbnail */}
        <div className="h-[120px] w-full flex-shrink-0 overflow-hidden rounded sm:h-[100px] sm:w-[140px]">
          <EstateImage
            className="h-full w-full object-cover"
            {...(props.thumbnailId ? { thumbnailId: props.thumbnailId } : {})}
            iconSize={28}
          />
        </div>
        {/* Content */}
        <div className="flex w-full flex-1 flex-col gap-3">
          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {props.externalId ? (
              <div className="rounded-2xl bg-[#07B1BE1F] px-3 py-[2px] text-sm text-primary-dark">
                {'#' + props.externalId}
              </div>
            ) : null}
            {propertyType ? (
              <div className="rounded-2xl bg-[#07B1BE1F] px-3 py-[2px] text-sm text-primary-dark">
                {propertyType}
              </div>
            ) : null}
            {marketType ? (
              <div className="rounded-2xl bg-[#07B1BE1F] px-3 py-[2px] text-sm text-primary-dark">
                {marketType}
              </div>
            ) : null}
            {props.status ? (
              <div className="rounded-2xl bg-[#07B1BE1F] px-3 py-[2px] text-sm text-primary-dark">
                {props.status === StatusType.draft ? t('draft') : t('public')}
              </div>
            ) : null}
          </div>

          {/* Details Row */}
          <div className="grid grid-cols-2 gap-2 text-sm text-primary-dark sm:flex sm:items-center sm:justify-between sm:gap-4">
            {/* Title */}
            <div className="text-sm font-bold leading-tight">{props.name}</div>
            {/* Price */}
            <div>{getRenderValue(props.price)}</div>

            {/* Area */}
            <div>
              {getRenderValue(props.livingArea)} m<sup>2</sup>
            </div>

            {/* Rooms */}
            <div>{getRenderValue(props.roomsCount)}</div>

            {/* Building */}
            {showBuilding ? <div>{props.building}</div> : null}

            {/* Creator */}
            <div className="whitespace-nowrap">{props.creator}</div>

            {/* Created At */}
            <div className="whitespace-nowrap">{getDate(props.createdAt)}</div>

            {/* Action */}
            {props.btnActionRender ? (
              <div className="flex sm:justify-end">{props.btnActionRender(props)}</div>
            ) : null}
          </div>

          {/* Address (below details row) */}
          {props.address?.address ? (
            <div className="truncate text-sm text-gray-700">{props.address?.address}</div>
          ) : null}
        </div>
      </Card>
    </Link>
  );
};
